import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Switch } from '@/components/ui/Switch/Switch';
import { Loader2, X, Plus, CalendarDays, Hash, ChevronLeft } from 'lucide-react';
import { useNotificationStore } from '@/stores/notificationStore';
import { agendaService } from '@/services/AgendaService';
import { AgendaPalette } from './AgendaPalette';
import { AgendaDayColumn, TimeRuler } from './AgendaDayColumn';
import { AgendaBlockEditor } from './AgendaBlockEditor';
import { AddBlockPicker } from './AddBlockPicker';
import {
  type AgendaBlock,
  type AgendaDayMode,
  type EventAgenda,
  type PresetCategory,
  type PresetChip,
  buildDayHeaders,
  computeEndTime,
  timeToMinutes,
  minutesToTime,
  snapMinutes,
  makeBlockId,
  addDays,
  GRID_START_HOUR,
  GRID_END_HOUR,
  PX_PER_MINUTE,
} from './constants';

export interface AgendaManagerProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
}

const GRID_START_MIN = GRID_START_HOUR * 60;
const GRID_END_MIN = GRID_END_HOUR * 60;

export function AgendaManager({ isOpen, onClose, eventId }: AgendaManagerProps) {
  const notificationStore = useNotificationStore();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [agendaId, setAgendaId] = useState<string>('');
  const [title, setTitle] = useState('Prelim Agenda');
  const [enabled, setEnabled] = useState(false);
  const [dayMode, setDayMode] = useState<AgendaDayMode>('date_range');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [dayCount, setDayCount] = useState(3);
  const [blocks, setBlocks] = useState<AgendaBlock[]>([]);

  const [editingBlock, setEditingBlock] = useState<AgendaBlock | null>(null);
  const [adding, setAdding] = useState<{ dayNumber: number; startTime: string } | null>(null);
  const [activeDrag, setActiveDrag] = useState<
    | { type: 'palette'; color: string; label: string }
    | { type: 'block'; block: AgendaBlock }
    | null
  >(null);

  const dayRefs = useRef<Record<number, HTMLElement | null>>({});
  const pointerRef = useRef<{ y: number }>({ y: 0 });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // ── Load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !eventId) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const agenda = await agendaService.getAgenda(eventId);
        if (cancelled) return;
        applyAgenda(agenda);
        setIsDirty(false);
      } catch (err) {
        console.error('Failed to load agenda:', err);
        notificationStore.error('Failed to load agenda', { title: 'Error' });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventId]);

  // Reset transient sub-dialogs whenever the manager is closed, so reopening
  // starts clean (the component stays mounted across open/close).
  useEffect(() => {
    if (!isOpen) {
      setEditingBlock(null);
      setAdding(null);
    }
  }, [isOpen]);

  function applyAgenda(a: EventAgenda) {
    setAgendaId(a.is_default ? '' : a.id);
    setTitle(a.title);
    setEnabled(a.enabled);
    setDayMode(a.day_mode);
    setStartDate(a.start_date);
    setDayCount(a.day_count);
    setBlocks(Array.isArray(a.blocks) ? a.blocks : []);
  }

  const markDirty = () => setIsDirty(true);

  // ── Day headers ───────────────────────────────────────────────────────────
  const dayHeaders = useMemo(
    () => buildDayHeaders(dayMode, startDate, dayCount),
    [dayMode, startDate, dayCount]
  );

  const blocksByDay = useCallback(
    (day: number) => blocks.filter((b) => b.day_number === day).sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time)),
    [blocks]
  );

  // ── Block mutations ────────────────────────────────────────────────────────
  const upsertBlock = (next: AgendaBlock) => {
    setBlocks((prev) => {
      const exists = prev.some((b) => b.id === next.id);
      return exists ? prev.map((b) => (b.id === next.id ? next : b)) : [...prev, next];
    });
    markDirty();
  };

  const deleteBlock = (id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    markDirty();
    setEditingBlock(null);
  };

  const resizeBlock = (id: string, durationMinutes: number) => {
    setBlocks((prev) =>
      prev.map((b) =>
        b.id === id
          ? { ...b, duration_minutes: durationMinutes, end_time: computeEndTime(b.start_time, durationMinutes) }
          : b
      )
    );
    markDirty();
  };

  const createBlock = (
    dayNumber: number,
    startMinutes: number,
    categoryKey: string,
    color: string,
    name: string,
    duration: number
  ) => {
    const clampedStart = Math.min(Math.max(startMinutes, GRID_START_MIN), GRID_END_MIN - duration);
    const start = minutesToTime(snapMinutes(clampedStart));
    const block: AgendaBlock = {
      id: makeBlockId(),
      day_number: dayNumber,
      start_time: start,
      end_time: computeEndTime(start, duration),
      duration_minutes: duration,
      name,
      category_key: categoryKey,
      color,
      description: null,
      sort_order: blocks.length,
    };
    upsertBlock(block);
  };

  // ── DnD ───────────────────────────────────────────────────────────────────
  const trackPointer = useCallback((e: PointerEvent) => {
    pointerRef.current = { y: e.clientY };
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    window.addEventListener('pointermove', trackPointer);
    const data = event.active.data.current as any;
    if (data?.type === 'palette') {
      setActiveDrag({ type: 'palette', color: data.color, label: data.chip.name });
    } else if (data?.type === 'block') {
      setActiveDrag({ type: 'block', block: data.block });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    window.removeEventListener('pointermove', trackPointer);
    setActiveDrag(null);
    const { active, over, delta } = event;
    const data = active.data.current as any;
    const overData = over?.data.current as any;

    if (data?.type === 'palette') {
      if (!overData || overData.type !== 'day') return;
      const dayNumber = overData.dayNumber as number;
      const el = dayRefs.current[dayNumber];
      let startMin = GRID_START_MIN + 9 * 60 - GRID_START_MIN; // fallback ~09:00 region
      if (el) {
        const rect = el.getBoundingClientRect();
        startMin = (pointerRef.current.y - rect.top) / PX_PER_MINUTE + GRID_START_MIN;
      } else {
        startMin = 9 * 60;
      }
      createBlock(dayNumber, startMin, data.categoryKey, data.color, data.chip.name, data.chip.duration);
      return;
    }

    if (data?.type === 'block') {
      const block = data.block as AgendaBlock;
      const newDay = overData?.type === 'day' ? (overData.dayNumber as number) : block.day_number;
      const deltaMin = snapMinutes(delta.y / PX_PER_MINUTE);
      let newStart = timeToMinutes(block.start_time) + deltaMin;
      newStart = Math.min(Math.max(newStart, GRID_START_MIN), GRID_END_MIN - block.duration_minutes);
      const start = minutesToTime(newStart);
      upsertBlock({
        ...block,
        day_number: newDay,
        start_time: start,
        end_time: computeEndTime(start, block.duration_minutes),
      });
    }
  };

  // ── Day controls ────────────────────────────────────────────────────────────
  const addDayAfter = () => {
    setDayCount((c) => c + 1);
    markDirty();
  };

  const addDayBefore = () => {
    setBlocks((prev) => prev.map((b) => ({ ...b, day_number: b.day_number + 1 })));
    setDayCount((c) => c + 1);
    if (dayMode === 'date_range' && startDate) setStartDate(addDays(startDate, -1));
    markDirty();
  };

  const removeLastDay = () => {
    if (dayCount <= 1) return;
    const newCount = dayCount - 1;
    setBlocks((prev) => prev.filter((b) => b.day_number <= newCount));
    setDayCount(newCount);
    markDirty();
  };

  const switchDayMode = (mode: AgendaDayMode) => {
    setDayMode(mode);
    markDirty();
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      setIsSaving(true);
      const saved = await agendaService.saveAgenda(eventId, {
        enabled,
        title: title.trim() || 'Prelim Agenda',
        day_mode: dayMode,
        start_date: dayMode === 'date_range' ? startDate : null,
        day_count: dayCount,
        blocks,
      });
      applyAgenda(saved);
      setIsDirty(false);
      notificationStore.success('Agenda saved', { title: 'Saved' });
    } catch (err) {
      console.error('Failed to save agenda:', err);
      notificationStore.error('Failed to save agenda', { title: 'Error' });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="fullScreen" closeOnEscape={false}>
      {isLoading ? (
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          <span className="ml-3 text-gray-600">Loading agenda…</span>
        </div>
      ) : (
        <div className="flex flex-col h-screen">
          {/* Header */}
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-5 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <input
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    markDirty();
                  }}
                  className="text-lg font-semibold text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-teal-500 focus:outline-none px-1"
                />
                {isDirty && <span className="text-amber-600 text-sm font-medium">• Unsaved</span>}
              </div>

              <div className="flex items-center gap-4">
                <Switch
                  label="Show on proposal"
                  checked={enabled}
                  onChange={(e) => {
                    setEnabled(e.target.checked);
                    markDirty();
                  }}
                />
                <button
                  onClick={onClose}
                  className="rounded-md p-1.5 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Day controls */}
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                <button
                  onClick={() => switchDayMode('date_range')}
                  className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium ${
                    dayMode === 'date_range' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600'
                  }`}
                >
                  <CalendarDays className="w-3.5 h-3.5" /> Dates
                </button>
                <button
                  onClick={() => switchDayMode('day_count')}
                  className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium ${
                    dayMode === 'day_count' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600'
                  }`}
                >
                  <Hash className="w-3.5 h-3.5" /> Day count
                </button>
              </div>

              {dayMode === 'date_range' && (
                <input
                  type="date"
                  value={startDate ?? ''}
                  onChange={(e) => {
                    setStartDate(e.target.value || null);
                    markDirty();
                  }}
                  className="rounded-md border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500"
                  title="First day's date"
                />
              )}

              <div className="flex items-center gap-1.5">
                <Button variant="ghost" size="sm" onClick={addDayBefore} leftIcon={<ChevronLeft className="w-3.5 h-3.5" />}>
                  Day before
                </Button>
                <span className="text-xs text-gray-500">{dayCount} days</span>
                <Button variant="ghost" size="sm" onClick={removeLastDay}>
                  −
                </Button>
                <Button variant="ghost" size="sm" onClick={addDayAfter} leftIcon={<Plus className="w-3.5 h-3.5" />}>
                  Day after
                </Button>
              </div>
            </div>
          </div>

          {/* Body */}
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="flex-1 flex overflow-hidden">
              <AgendaPalette />
              <div className="flex-1 overflow-auto">
                <div className="flex min-w-fit">
                  <TimeRuler />
                  {dayHeaders.map((header) => (
                    <AgendaDayColumn
                      key={header.dayNumber}
                      header={header}
                      blocks={blocksByDay(header.dayNumber)}
                      registerDayRef={(n, el) => {
                        dayRefs.current[n] = el;
                      }}
                      onBlockClick={(b) => setEditingBlock(b)}
                      onBlockResize={resizeBlock}
                      onEmptyClick={(dayNumber, startTime) => setAdding({ dayNumber, startTime })}
                    />
                  ))}
                </div>
              </div>
            </div>

            <DragOverlay>
              {activeDrag ? (
                <div
                  className="rounded-md px-2 py-1 text-xs font-medium shadow-lg"
                  style={{
                    backgroundColor: activeDrag.type === 'palette' ? `${activeDrag.color}` : `${activeDrag.block.color}`,
                    color: '#fff',
                  }}
                >
                  {activeDrag.type === 'palette' ? activeDrag.label : activeDrag.block.name}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-5 py-3 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {enabled ? 'Visible on the client proposal once saved.' : 'Hidden from the client proposal.'}
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose} disabled={isSaving}>
                Close
              </Button>
              <Button variant="primary" onClick={handleSave} isLoading={isSaving} disabled={!isDirty}>
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Block editor */}
      {editingBlock && (
        <AgendaBlockEditor
          block={editingBlock}
          onSave={(b) => {
            upsertBlock(b);
            setEditingBlock(null);
          }}
          onDelete={deleteBlock}
          onClose={() => setEditingBlock(null)}
        />
      )}

      {/* Add via empty-slot click */}
      {adding && (
        <AddBlockPicker
          dayNumber={adding.dayNumber}
          startTime={adding.startTime}
          onPick={(category: PresetCategory, chip: PresetChip) => {
            createBlock(
              adding.dayNumber,
              timeToMinutes(adding.startTime),
              category.key,
              category.color,
              chip.name,
              chip.duration
            );
            setAdding(null);
          }}
          onClose={() => setAdding(null)}
        />
      )}
    </Modal>
  );
}

export default AgendaManager;
